import type { ReactNode } from "react"

import { CREATOR_APP_COLUMN_STICKY } from "@/lib/creator-layout"
import { cn } from "@/lib/utils"

/**
 * OKS-699 — Mission-detail primary CTA bar.
 *
 * Replaces the OKS-478 sheet's pinned footer now that mission detail renders
 * as a regular full page in the creator shell (no more `<MissionSheet>`).
 *
 * Placement (UX Spec §2/§5):
 *   - A full-width sticky bar pinned to the viewport ABOVE the fixed
 *     `<TabBar>`, at EVERY width. OKS-702 — the tab nav is
 *     `4rem + env(safe-area-inset-bottom)` tall, so this bar sits at
 *     `fixed bottom-[calc(4rem+env(safe-area-inset-bottom))]` to stay flush
 *     above it on home-indicator devices. It carries a top border + solid
 *     background so content scrolls under it. The page column pads its bottom
 *     to clear both this bar and the tab nav (see `MissionDetail`).
 *
 * OKS-739 — the previous `≥ lg` `static`/inline branch is GONE. The bottom-tab
 * nav now persists at all widths (iPad included), so the CTA must keep tracking
 * it above the bar at `lg+` too — dropping to `static` there would let the CTA
 * scroll away while the fixed nav stayed, breaking the "CTA sits above the nav"
 * contract on iPad. The inner content caps + centers to the shared creator
 * app-column (`CREATOR_APP_COLUMN_STICKY`) so it aligns with the centered
 * content + tab row on wide screens. Renders nothing when no CTA is supplied
 * (e.g. loading/error states), so callers can mount it conditionally without an
 * empty bar.
 */
export interface MissionCtaBarProps {
  /** The primary CTA node (gifting `CTAButton` or `LifecycleView`). */
  children?: ReactNode
  className?: string
}

export function MissionCtaBar({ children, className }: MissionCtaBarProps) {
  if (!children) return null

  return (
    <div
      data-testid="mission-detail-cta-bar"
      className={cn(
        // Sticky bar above the fixed tab nav at ALL widths. OKS-702 — the tab
        // nav is `4rem + env(safe-area-inset-bottom)` tall, so pin this bar at
        // that offset (was `bottom-16`) to stay flush above it without
        // overlapping the home-indicator inset. In-browser inset 0 → 4rem, i.e.
        // the previous `bottom-16`. OKS-739 — the `lg:static` desktop flip is
        // removed so the CTA keeps tracking the (now always-present) tab nav.
        "bg-background fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-20 border-t p-4",
        className,
      )}
    >
      {/* Cap + center to the shared creator app-column so the CTA aligns with
          the centered content + tab row (OKS-739). */}
      <div className={CREATOR_APP_COLUMN_STICKY}>{children}</div>
    </div>
  )
}
